/**
 * The verdict panel — and it is SINK-AWARE by design.
 *
 * 8/8 collector roles up while the ES video index has had no writes for 11 days
 * is NOT healthy, and a board that shows green there is worse than no board: it
 * actively reassures you. Role liveness and sink freshness both feed one verdict.
 */
import { useEsHealth, useHealth } from '../api/queries'
import { StatusRing } from '../components/charts'
import { PanelState } from '../components/ui'
import { age } from '../components/format'

export function StatusBand() {
  const health = useHealth()
  const es = useEsHealth()

  const roles = health.data?.roles ?? []
  const alive = roles.filter((r) => r.alive > 0).length

  // Sink staleness is a first-class input, not a footnote in another panel.
  const staleSinks =
    es.data && 'indices' in es.data
      ? Object.entries(es.data.indices)
          .filter(([, v]) => 'stale' in v && v.stale)
          .map(([k, v]) => `${k} index ${age('age_seconds' in v ? v.age_seconds : null)} stale`)
      : []

  const problems = [...(health.data?.problems ?? []), ...staleSinks]
  const state = problems.length ? (staleSinks.length ? 'crit' : 'warn') : 'ok'
  const verdict = problems.length
    ? staleSinks.length
      ? `Degraded — ${staleSinks.length} sink stale`
      : 'Degraded'
    : 'All systems nominal'

  return (
    <div className="band">
      <PanelState isLoading={health.isLoading} error={health.error}>
        <div className="ring">
          {/* "groups", not "roles": /metrics/health returns one entry per stream
              WITH a consumer group — 5 of the 6 streams, since dead_letter has
              none. Labelling it "roles" claimed to cover all 9, leaving api,
              api-job, coverage and scheduler silently unrepresented. */}
          <StatusRing value={alive} of={roles.length} state={state} caption="groups" />
          <div>
            <div className="ring-t" style={{ color: `var(--${state})` }}>{verdict}</div>
            <ul className="ring-s mono">
              <li>{alive}/{roles.length} consumer groups live</li>
              <li className="ring-caveat">worker roles without a stream group are not covered here</li>
              <li>{health.data?.dead_letter ?? 0} dead-lettered · {health.data?.throttled_today ?? 0} throttles today</li>
              {problems.map((p) => <li key={p} style={{ color: `var(--${state})` }}>{p}</li>)}
            </ul>
          </div>
        </div>
      </PanelState>
    </div>
  )
}
